<template>
    <div class="elementsTree">
        <p class="ot-title">{{ $t('dash.post-creator.media-editor.elements-tree.title') }}</p>
        <div class="et-list">
            <template v-for="item in displayItems">
                <!-- Header + members share one wrapping block so the connector line (below) can
                     span the whole group as a single continuous line, rather than being redrawn
                     per member row with a gap at each boundary (the .et-list gap between flex
                     siblings that broke it up when members rendered as plain top-level rows). -->
                <div v-if="item.kind === 'group'" :key="item.key" class="et-group">
                    <div
                        class="et-row et-group-header"
                        :class="{ selected: item.groupId === selectedGroupId }"
                        @click="$emit('select-group', item.groupId)"
                    >
                        <i
                            class="fa-solid et-collapse-icon"
                            :class="isCollapsed(item.groupId) ? 'fa-chevron-right' : 'fa-chevron-down'"
                            @click.stop="toggleCollapsed(item.groupId)"
                        ></i>
                        <div class="et-icon et-group-icon"><i class="fa-solid fa-object-group"></i></div>
                        <span class="et-label">{{ $t('dash.post-creator.media-editor.elements-tree.group-label', { count: item.members.length }) }}</span>
                        <div class="et-actions">
                            <i class="fa-solid fa-clone" :title="$t('dash.post-creator.media-editor.elements-tree.duplicate-group')" @click.stop="$emit('duplicate-group', item.groupId)"></i>
                            <i class="fa-solid fa-object-ungroup" :title="$t('dash.post-creator.media-editor.elements-tree.ungroup')" @click.stop="$emit('ungroup', item.groupId)"></i>
                            <i class="fa-solid fa-trash" :title="$t('dash.post-creator.media-editor.elements-tree.remove-group')" @click.stop="$emit('remove-group', item.groupId)"></i>
                        </div>
                    </div>

                    <div class="et-group-members" v-if="!isCollapsed(item.groupId)">
                        <div
                            v-for="member in item.members"
                            :key="member.id"
                            class="et-row et-row-member"
                            :class="{ selected: member.id === selectedOverlayId }"
                            @click="$emit('select', member.id)"
                        >
                            <div class="et-icon" :style="{ backgroundColor: overlayColor(member) }"><i :class="overlayIcon(member)"></i></div>
                            <span class="et-label">{{ overlayLabel(member) }}</span>
                            <div class="et-actions">
                                <i class="fa-solid fa-angle-up" :title="$t('dash.post-creator.media-editor.elements-tree.forward')" @click.stop="$emit('reorder', { overlayId: member.id, direction: 'forward' })"></i>
                                <i class="fa-solid fa-angle-down" :title="$t('dash.post-creator.media-editor.elements-tree.backward')" @click.stop="$emit('reorder', { overlayId: member.id, direction: 'backward' })"></i>
                                <i class="fa-solid fa-clone" :title="$t('dash.post-creator.media-editor.elements-tree.duplicate')" @click.stop="$emit('duplicate', member.id)"></i>
                                <i class="fa-solid fa-trash" :title="$t('dash.post-creator.media-editor.elements-tree.remove')" @click.stop="$emit('remove', member.id)"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-else
                    :key="item.key"
                    class="et-row"
                    :class="{ selected: item.overlay.id === selectedOverlayId }"
                    @click="$emit('select', item.overlay.id)"
                >
                    <div class="et-icon" :style="{ backgroundColor: overlayColor(item.overlay) }"><i :class="overlayIcon(item.overlay)"></i></div>
                    <span class="et-label">{{ overlayLabel(item.overlay) }}</span>
                    <div class="et-actions">
                        <i class="fa-solid fa-angle-up" :title="$t('dash.post-creator.media-editor.elements-tree.forward')" @click.stop="$emit('reorder', { overlayId: item.overlay.id, direction: 'forward' })"></i>
                        <i class="fa-solid fa-angle-down" :title="$t('dash.post-creator.media-editor.elements-tree.backward')" @click.stop="$emit('reorder', { overlayId: item.overlay.id, direction: 'backward' })"></i>
                        <i class="fa-solid fa-clone" :title="$t('dash.post-creator.media-editor.elements-tree.duplicate')" @click.stop="$emit('duplicate', item.overlay.id)"></i>
                        <i class="fa-solid fa-trash" :title="$t('dash.post-creator.media-editor.elements-tree.remove')" @click.stop="$emit('remove', item.overlay.id)"></i>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { WIDGET_ACCENT_COLORS } from '../engine/constants.js'
import { getWidgetDisplayText } from '../engine/widgets/widgetRenderers.js'

const WIDGET_ICONS = {
    place: 'fa-solid fa-location-dot',
    datetime: 'fa-solid fa-clock',
    weather: 'fa-solid fa-sun',
    gif: 'fa-solid fa-film',
    music: 'fa-solid fa-music',
    hashtag: 'fa-solid fa-hashtag',
    mention: 'fa-solid fa-at'
}

export default {
    name: 'ElementsTree',

    props: {
        overlays: {
            type: Array,
            default: () => []
        },
        selectedOverlayId: {
            type: String,
            default: null
        },
        // Set only while a *complete* group is the canvas's current multi-selection (see
        // MediaEditor.vue's onMultiSelectionChange) - a diagnostic readout of that state,
        // independent of whatever the canvas's own frame actually renders for the same selection.
        selectedGroupId: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            collapsedGroupIds: []
        }
    },

    computed: {
        // Array order is z-order, front = last (see reorderOverlayInTrack) - a layers panel reads
        // top-to-bottom as front-to-back, so the list itself is reversed, not the underlying data.
        reversedOverlays() {
            return this.overlays.slice().reverse()
        },

        // One item per plain overlay, one per group (carrying its own members array, rendered as
        // a single wrapping block - see the template) - grouping is purely a display concern here,
        // the underlying overlays array/z-order never gets reshuffled into contiguous blocks.
        displayItems() {
            const items = []
            const seenGroups = new Set()
            this.reversedOverlays.forEach((overlay) => {
                if (!overlay.groupId) {
                    items.push({ key: overlay.id, kind: 'overlay', overlay })
                    return
                }
                if (seenGroups.has(overlay.groupId)) return
                seenGroups.add(overlay.groupId)
                const members = this.reversedOverlays.filter(o => o.groupId === overlay.groupId)
                items.push({ key: 'group-' + overlay.groupId, kind: 'group', groupId: overlay.groupId, members })
            })
            return items
        }
    },

    methods: {
        overlayIcon(overlay) {
            if (overlay.type === 'widget') return WIDGET_ICONS[overlay.widgetType] || 'fa-solid fa-icons'
            if (overlay.type === 'shape') return 'fa-solid fa-shapes'
            if (overlay.type === 'image') return 'fa-solid fa-image'
            return 'fa-solid fa-font'
        },

        overlayColor(overlay) {
            if (overlay.type === 'widget') return WIDGET_ACCENT_COLORS[overlay.widgetType] || '#5c5c5c'
            if (overlay.type === 'shape') return '#8a8a8a'
            return '#2f1937'
        },

        overlayLabel(overlay) {
            const data = overlay.data || {}
            if (overlay.type === 'widget') return getWidgetDisplayText(overlay) || this.$t('dash.post-creator.media-editor.track-row.fallback-widget')
            if (overlay.type === 'shape') return `${this.$t('dash.post-creator.media-editor.track-row.type-shape')} : ${data.shape || ''}`
            if (overlay.type === 'image') return this.$t('dash.post-creator.media-editor.elements-tree.type-image')
            return `${this.$t('dash.post-creator.media-editor.track-row.type-text')} : ${(data.text || '').slice(0, 24)}`
        },

        isCollapsed(groupId) {
            return this.collapsedGroupIds.includes(groupId)
        },

        toggleCollapsed(groupId) {
            const index = this.collapsedGroupIds.indexOf(groupId)
            if (index === -1) this.collapsedGroupIds.push(groupId)
            else this.collapsedGroupIds.splice(index, 1)
        }
    }
}
</script>

<style lang="scss" scoped></style>
